import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Spinner, Alert, Tabs, Tab } from 'react-bootstrap';
import { api } from '../services/api';

interface AnalystUser {
  id: string;
  username: string;
  email: string;
}

interface SubjectTransferModalProps {
  show: boolean;
  subjectId: number | null;
  subjectName: string;
  onHide: () => void;
  onSuccess: () => void;
}

interface StatusItem {
  id: string;
  name: string;
}

interface BatchStatusSummary {
  matched: number;
  updated: number;
  failed: number;
}

const SubjectTransferModal: React.FC<SubjectTransferModalProps> = ({
  show,
  subjectId,
  subjectName,
  onHide,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [users, setUsers] = useState<AnalystUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'transfer' | 'batch-status'>('transfer');
  const [statusesLoading, setStatusesLoading] = useState(false);
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [fromStatusId, setFromStatusId] = useState('');
  const [toStatusId, setToStatusId] = useState('');
  const [statusChanging, setStatusChanging] = useState(false);
  const [statusChangeError, setStatusChangeError] = useState<string | null>(null);
  const [statusChangeSummary, setStatusChangeSummary] = useState<BatchStatusSummary | null>(null);

  useEffect(() => {
    if (show && subjectId) {
      loadUsers();
      loadStatuses();
    } else {
      // Сброс состояния при закрытии
      setUsers([]);
      setSelectedUserId('');
      setError(null);
      setActiveTab('transfer');
      setStatuses([]);
      setFromStatusId('');
      setToStatusId('');
      setStatusChangeError(null);
      setStatusChangeSummary(null);
    }
  }, [show, subjectId]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getAnalystUsers();
      setUsers(response.data || []);
    } catch (err: any) {
      console.error('Failed to load users:', err);
      setError('Не удалось загрузить список пользователей');
    } finally {
      setLoading(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedUserId || !subjectId) {
      setError('Выберите пользователя для передачи');
      return;
    }

    try {
      setTransferring(true);
      setError(null);
      await api.transferSubject(subjectId, selectedUserId);
      onSuccess();
      onHide();
    } catch (err: any) {
      console.error('Failed to transfer subject:', err);
      const errorMessage = err.response?.data?.error || 'Не удалось передать предмет';
      setError(errorMessage);
    } finally {
      setTransferring(false);
    }
  };

  const loadStatuses = async () => {
    try {
      setStatusesLoading(true);
      const response = await api.getStatuses();
      setStatuses(response.data?.data || []);
    } catch (err) {
      console.error('Failed to load statuses:', err);
      setStatusChangeError('Не удалось загрузить статусы');
    } finally {
      setStatusesLoading(false);
    }
  };

  const isBatchActionDisabled = useMemo(
    () =>
      statusChanging ||
      statusesLoading ||
      !subjectId ||
      !fromStatusId ||
      !toStatusId ||
      fromStatusId === toStatusId,
    [statusChanging, statusesLoading, subjectId, fromStatusId, toStatusId]
  );

  const handleBatchStatusChange = async () => {
    if (!subjectId || !fromStatusId || !toStatusId) {
      setStatusChangeError('Выберите текущий и новый статус');
      return;
    }

    if (fromStatusId === toStatusId) {
      setStatusChangeError('Текущий и новый статус должны отличаться');
      return;
    }

    try {
      setStatusChanging(true);
      setStatusChangeError(null);
      setStatusChangeSummary(null);

      const response = await api.changeSubjectProductsStatus(subjectId, fromStatusId, toStatusId);
      setStatusChangeSummary(response.data?.summary || null);
      onSuccess();
    } catch (err: any) {
      console.error('Failed to change subject products status:', err);
      setStatusChangeError(err.response?.data?.error || 'Не удалось массово изменить статусы');
    } finally {
      setStatusChanging(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Управление нишей</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3">
          <strong>Предмет:</strong> {subjectName}
        </div>
        <Tabs
          activeKey={activeTab}
          onSelect={(key) => setActiveTab((key as 'transfer' | 'batch-status') || 'transfer')}
          className="mb-3"
        >
          <Tab eventKey="transfer" title="Передача ниши">
            {loading ? (
              <div className="text-center py-3">
                <Spinner animation="border" />
                <div className="mt-2">Загрузка списка пользователей...</div>
              </div>
            ) : error && !loading ? (
              <Alert variant="danger">{error}</Alert>
            ) : users.length === 0 ? (
              <Alert variant="info">Нет доступных пользователей для передачи</Alert>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Выберите пользователя:</Form.Label>
                  <Form.Select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    disabled={transferring}
                  >
                    <option value="">-- Выберите пользователя --</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.username} ({user.email})
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {error && (
                  <Alert variant="danger" className="mt-3">
                    {error}
                  </Alert>
                )}
              </>
            )}
          </Tab>

          <Tab eventKey="batch-status" title="Массовая смена статуса">
            {statusesLoading ? (
              <div className="text-center py-3">
                <Spinner animation="border" />
                <div className="mt-2">Загрузка статусов...</div>
              </div>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Текущий статус:</Form.Label>
                  <Form.Select
                    value={fromStatusId}
                    onChange={(e) => setFromStatusId(e.target.value)}
                    disabled={statusChanging}
                  >
                    <option value="">-- Выберите текущий статус --</option>
                    {statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Новый статус:</Form.Label>
                  <Form.Select
                    value={toStatusId}
                    onChange={(e) => setToStatusId(e.target.value)}
                    disabled={statusChanging}
                  >
                    <option value="">-- Выберите новый статус --</option>
                    {statuses.map((status) => (
                      <option key={status.id} value={status.id}>
                        {status.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {statusChangeError && (
                  <Alert variant="danger">{statusChangeError}</Alert>
                )}

                {statusChangeSummary && (
                  <Alert variant="success">
                    Обработано: {statusChangeSummary.matched}, обновлено: {statusChangeSummary.updated}, ошибок: {statusChangeSummary.failed}
                  </Alert>
                )}
              </>
            )}
          </Tab>
        </Tabs>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={transferring || statusChanging}>
          Отмена
        </Button>
        {activeTab === 'transfer' ? (
          <Button
            variant="primary"
            onClick={handleTransfer}
            disabled={transferring || !selectedUserId || loading}
          >
            {transferring ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Передача...
              </>
            ) : (
              'Передать'
            )}
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handleBatchStatusChange}
            disabled={isBatchActionDisabled}
          >
            {statusChanging ? (
              <>
                <Spinner size="sm" animation="border" className="me-2" />
                Изменение...
              </>
            ) : (
              'Ок'
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SubjectTransferModal;



